import { Controller } from "@hotwired/stimulus"
import db from "../field_db"
import { logEvent } from "../field_logger"
import { BLOB_UNREADABLE_FAIL_STATUS, TOO_LARGE_FAIL_STATUS } from "../field_image_queue"

// "Needs attention" list on /field/sync (card #4005). The red failures banner
// points here: it lists every failed photo and survey answer in the local
// queue with a plain-language reason, and lets the tasker clear photos that
// are beyond recovery so the red banner stops showing.
//
// Rows are cloned from the <template> in field/sync/_needs_attention.html.erb
// and filled with textContent -- no HTML is built here.
//
// Clearing removes local queue rows only, and only photo rows that are
// status "failed" with a non-zero failStatus (unreadable, too large, or
// rejected by the server). A failStatus of 0 is a network failure the sync
// pass re-arms, so it is listed but never cleared. Pending and uploading
// rows are never touched. Survey answers are listed, never cleared: they hold
// the tasker's work and need support.
//
// Targets: section (outer wrapper, hidden when empty), count, list, clearBar,
// row (the <template>).

export default class extends Controller {
  static targets = ["section", "count", "list", "clearBar", "row"]

  connect() {
    this._refresh = () => this._render()
    window.addEventListener("field:queue-changed", this._refresh)
    window.addEventListener("field:sync-complete", this._refresh)
    this._render()
  }

  disconnect() {
    window.removeEventListener("field:queue-changed", this._refresh)
    window.removeEventListener("field:sync-complete", this._refresh)
  }

  async clear() {
    const rows = await db.pending_images.where("status").equals("failed").toArray()

    for (const row of rows) {
      if (!this._clearable(row)) continue

      // Re-read just before deleting: the row may have been re-armed or
      // retried since the list was drawn.
      const current = await db.pending_images.get(row.id)
      if (!this._clearable(current)) continue

      await db.pending_images.delete(row.id)
      logEvent("sync", "Failed item cleared", {
        kind: "photo",
        responseId: row.surveyableId,
        failStatus: row.failStatus
      }).catch(() => {})
    }

    await this._render()
  }

  _clearable(row) {
    return row?.status === "failed" && row.failStatus !== 0
  }

  async _render() {
    const [images, responses, queuedResponses] = await Promise.all([
      db.pending_images.where("status").equals("failed").toArray(),
      db.pending_responses.where("status").equals("failed").toArray(),
      db.pending_responses.toArray()
    ])

    const placeNames = new Map(queuedResponses.map(r => [String(r.responseId), r.placeName]))
    const items = [
      ...images.map(row => ({
        title: placeNames.get(String(row.surveyableId)) || "Photo",
        reason: this._photoReason(row)
      })),
      ...responses.map(row => ({
        title: row.placeName || "Survey answers",
        reason: "These answers could not be saved. Please contact support."
      }))
    ]

    this.listTarget.replaceChildren(...items.map(item => this._row(item)))
    this.countTarget.textContent = items.length > 0 ? String(items.length) : ""
    this.sectionTarget.classList.toggle("hidden", items.length === 0)
    this.clearBarTarget.classList.toggle("hidden", !images.some(row => this._clearable(row)))
  }

  _row({ title, reason }) {
    const fragment = this.rowTarget.content.cloneNode(true)
    fragment.querySelector("[data-slot='title']").textContent = title
    fragment.querySelector("[data-slot='reason']").textContent = reason
    return fragment
  }

  _photoReason(row) {
    if (row.failStatus === BLOB_UNREADABLE_FAIL_STATUS) return "This photo can't be read from your phone any more. Please retake it."
    if (row.failStatus === TOO_LARGE_FAIL_STATUS) return "This photo was too large to upload. Please retake it."
    if (row.failStatus === 0) return "Couldn't reach the server. It will retry when you have signal."
    return `The server rejected this photo (code ${row.failStatus}).`
  }
}
